/**
 * The single predicate that decides whether offline order capture is offered.
 *
 * Every refusal is a REASON, never a silent disable — the cashier must be able
 * to tell "the feature is off" from "this terminal isn't prepared", because the
 * fixes are completely different. A greyed-out button with no explanation is
 * what this whole feature exists to replace.
 */

export interface OfflineCapabilityInput {
  /**
   * `navigator.onLine`. Correct when the adapter genuinely drops, but it is
   * NOT sufficient on its own — see `backendReachable`.
   */
  online: boolean
  /**
   * Whether the backend actually answered last time we asked (F-FE-N30).
   *
   * This is the load-bearing signal. In the outage restaurants really have —
   * router up, ISP down — `online` stays true the whole time, so gating on it
   * alone meant offline capture never engaged in the exact scenario it exists
   * for. Defaults to true so a terminal that has not failed anything yet is
   * treated as connected.
   */
  backendReachable?: boolean
  /**
   * Whether this shell can print a kitchen ticket silently to a named station
   * printer (F-FE-N123) — true in the Electron desktop app and in the Bhookly
   * Server Android app, false in any browser tab. Decided by
   * `lib/offline/shell.ts`; this predicate only asks for the capability.
   */
  canPrintSilently: boolean
  /** When station routing was last cached, or null if never. */
  stationsCachedAt: number | null
  /** True when any active station is output_type='kds' — unreachable offline. */
  hasKdsStation: boolean
}

/**
 * F-CTRL-N52 removed `no_reserved_numbers`.
 *
 * It was the only refusal that could fire on a correctly-installed, correctly-
 * configured terminal in the middle of service, and it is what stopped a live
 * till on 2026-08-26. Its cause was structural: capture required a block of
 * order numbers keyed to a business date, and the business date is a server
 * value that cannot be read during the outage it is needed for. Offline slips
 * now carry a locally-minted ticket instead, so there is nothing left to be out
 * of and nothing left to refuse over.
 *
 * What remains are two conditions that are properties of the INSTALLATION, not
 * of the moment: can this shell print silently, and has it ever seen the
 * station routing. Both are stable across a restart, and both are actionable
 * while the terminal is still online — which a refusal has to be to be worth
 * having.
 *
 * F-FE-N123 renamed `needs_desktop_app` to `needs_native_shell`. The old name
 * described one shell that happened to qualify, and the copy behind it sent a
 * tablet till — which prints over the LAN with no internet at all — off to look
 * for a desktop app it does not need.
 */
export type OfflineRefusal = 'online' | 'needs_native_shell' | 'no_station_cache'

export interface OfflineCapability {
  ok: boolean
  reason?: OfflineRefusal
  warning?: 'kds_stations_unreachable'
}

export function offlineCaptureAvailable(i: OfflineCapabilityInput): OfflineCapability {
  // Connected means BOTH: the OS reports a network AND the backend actually
  // answered. Either one saying otherwise puts us in the offline path.
  //
  // Checking only `online` was the original bug: with the router up and the ISP
  // down, `navigator.onLine` never goes false, so the cashier got a
  // "cannot reach the server" error instead of the offline flow.
  const reachable = i.backendReachable !== false
  if (i.online && reachable) return { ok: false, reason: 'online' }

  // A browser tab cannot print silently to a named station printer, so firing
  // there would pop a dialog nobody is watching in a live kitchen. Capturing an
  // order the kitchen never sees is not the feature.
  if (!i.canPrintSilently) return { ok: false, reason: 'needs_native_shell' }

  // Without cached routing there is no way to know which printer a line
  // belongs to; guessing would send food tickets to the bar.
  if (i.stationsCachedAt == null) return { ok: false, reason: 'no_station_cache' }

  // A KDS board is a separate device reading Postgres, so it cannot receive an
  // offline ticket. Printer stations still work, so this is a warning rather
  // than a refusal — but the cashier must know to relay those items by hand.
  return i.hasKdsStation
    ? { ok: true, warning: 'kds_stations_unreachable' }
    : { ok: true }
}
